import { useContext, useEffect, useState } from 'react'
import { NVButton } from '@nv/basic-components'
import { Loader } from '@nv/basic-components'
import { TextField } from '@nv/basic-components'
import { DateOfBirthDropdown } from '@nv/components'
import { LoginFlowContext, useVoterUserContext } from '@nv/contexts'
import { useQuery } from '@nv/hooks'
import { VoterAuthService } from '@nv/services'
import { useFormik } from 'formik'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import * as Yup from 'yup'
import styles from './UserLoginOneKey.module.scss'

const initialValues = {
  username: '',
  dobMonth: '',
  dobDay: '',
  dobYear: '',
}

export function UserLoginOneKey({ findMemberNumber = false, helpEmail }: any) {
  const { t, i18n } = useTranslation()
  const currentLanguage = i18n.language
  const navigate = useNavigate()
  const { executeRecaptcha } = useGoogleReCaptcha()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const { setVoterUser } = useVoterUserContext()
  const query = useQuery()
  const accessCode = query.get('accessCode')

  const LOGIN_CONTEXT = useContext(LoginFlowContext)

  const loginSchema = Yup.object()
    .shape({
      username: Yup.string().required(t('form.validations.accessKeyRequired')),
      dobMonth: Yup.string().required(t('admin.users.monthRequired')),
      dobDay: Yup.string().required(t('admin.users.dayRequired')),
      dobYear: Yup.string().required(t('admin.users.yearRequired')),
    })
    .test('is-valid-date', function (value) {
      const { dobDay, dobMonth, dobYear } = value
      if (!dobDay || !dobMonth || !dobYear) return false
      const day = parseInt(dobDay)
      const month = parseInt(dobMonth)
      const year = parseInt(dobYear)
      const date = new Date(year, month - 1, day)
      const isValidDate = date.getDate() === day && date.getMonth() === month - 1 && date.getFullYear() === year
      return isValidDate || this.createError({ path: 'dobYear', message: t('registration.form.validations.validBirthDate') })
    })

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values) => {
      setLoading(true)
      try {
        if (!executeRecaptcha) {
          //console.log('Execute recaptcha not yet available')
          return
        }
        const token = await executeRecaptcha('voter_login')

        const userInput = {
          username: values.username.trim(),
          dateOfBirth: `${values.dobYear}-${values.dobMonth.toString().padStart(2, '0')}-${values.dobDay.toString().padStart(2, '0')}`,
        }
        const resp = await VoterAuthService.loginOneKey(userInput.username, userInput.dateOfBirth, '', token)

        if (resp?.success) {
          if (resp.user) {
            setVoterUser(resp.user)
          }
          if (!resp.link) {
            if (resp.passwordResetRequired) {
              LOGIN_CONTEXT.setLoginFlow('Forgot Password')
            }
            navigate('/voting/voter-verify/select-verifier')
          }
          setError(false)
        } else {
          setError(true)
          // console.error(resp.error?.message)
          switch (resp.error?.message) {
            case 'Invalid user credentials':
              setErrorMessage(t('form.validations.invalidCredentials'))
              break
            case 'session error: only one session per user':
              setErrorMessage(t('auth.login.errors.session'))
              break
            case 'Account disabled':
              setErrorMessage(t('auth.login.errors.accountDisabled'))
              break
            case 'Account locked.':
              setErrorMessage(t('auth.login.errors.accountLocked'))
              break

            case 'recaptcha error':
              setErrorMessage(t('auth.login.errors.recaptcha'))
              break
            case 'User has already voted.':
              setErrorMessage(t('auth.login.errors.alreadyVoted'))
              break
            case 'No active events.':
              setErrorMessage(t('auth.login.errors.noActiveEvents'))
              break
            default:
              setErrorMessage(t('form.validations.invalidCredentials'))
              break
          }
        }
      } catch (error) {
        setError(true)
      } finally {
        setLoading(false)
      }
    },
  })

  const formatUsernameWithSpaces = (value: string) => {
    const raw = value.replace(/[\s-]/g, '')
    return raw.replace(/(.{4})/g, '$1 ').trimEnd()
  }

  useEffect(() => {
    if (accessCode) {
      formik.setFieldValue('username', formatUsernameWithSpaces(accessCode))
    }
  }, [accessCode])

  return (
    <div className={styles['container']}>
      <form
        className={styles['form']}
        onSubmit={(e) => {
          e.preventDefault()
          formik.submitForm()
        }}
      >
        <span className={styles['title']}>{t('pages.login')}</span>

        <div className={styles['form__section']}>
          <TextField
            classes={{ container: styles['username-input'] }}
            formik={formik}
            field='username'
            placeholder={t('form.labels.enterOneKey')}
            label={t('form.labels.oneKey')}
            isRequired={true}
            onChange={(e) => formik.setFieldValue('username', formatUsernameWithSpaces(e.target.value))}
          />
        </div>
        <div className={styles['form__section']}>
          <DateOfBirthDropdown formik={formik} styles={styles} />
        </div>
        {error && (
          <span className={styles['errors']} role='alert'>
            {errorMessage}
          </span>
        )}

        {loading ? (
          <Loader />
        ) : (
          <NVButton
            classes={{ container: styles['button'], content: styles['content'] }}
            height={50}
            text={t('buttons.login')}
            onClick={() => formik.submitForm()}
            type='submit'
          />
        )}
      </form>
      {helpEmail && (
        <div className={styles['footer']}>
          {t('auth.help.needHelp')}{' '}
          <a target='_blank' rel='noreferrer' href={`mailto:${helpEmail}`}>
            {t('auth.help.contactUs')}
          </a>
        </div>
      )}
    </div>
  ) //
}
